import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { LogOut, ScrollText, Gauge, History, CreditCard, UserCircle, Shield, Menu, X } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const desktopLink = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 text-xs font-display font-bold uppercase tracking-[0.2em] border-b-2 transition-colors ${
      isActive
        ? "text-yellow-500 border-yellow-500"
        : "text-zinc-400 border-transparent hover:text-white hover:border-zinc-700"
    }`;

  const mobileLink = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3.5 font-display font-bold uppercase tracking-[0.18em] text-sm border-l-2 transition-colors ${
      isActive
        ? "text-yellow-500 border-yellow-500 bg-yellow-500/5"
        : "text-zinc-300 border-transparent hover:text-white hover:bg-zinc-900"
    }`;

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const close = () => setMenuOpen(false);

  const utc = now.toISOString().slice(11, 19);
  const isAdmin = user && typeof user === "object" && user.role === "admin";
  const subStatus = user?.subscription_status;
  const showStatusBadge = subStatus && subStatus !== "active" && !isAdmin;

  return (
    <>
      <header data-testid="app-header" className="sticky top-0 z-40 bg-zinc-950/90 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9 border border-zinc-800 text-zinc-300 hover:text-yellow-500 hover:border-yellow-500/40"
            aria-label="Apri menu"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-6 min-w-0">
            <NavLink to="/dashboard" className="flex items-center gap-3 group" data-testid="logo-link">
              <BrandLogo size={36} />
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="font-display font-black uppercase tracking-tight text-base text-white">
                  FIA <span className="text-yellow-500">Steward</span>
                </span>
                <span className="font-mono-tel text-[9px] text-emerald-400 tracking-[0.2em] uppercase">
                  Esports · Simracing Center
                </span>
              </div>
            </NavLink>
            <nav className="hidden md:flex items-center gap-1">
              <NavLink to="/dashboard" className={desktopLink} data-testid="nav-dashboard"><Gauge className="w-3.5 h-3.5" /> Analizza</NavLink>
              <NavLink to="/history" className={desktopLink} data-testid="nav-history"><History className="w-3.5 h-3.5" /> Storico</NavLink>
              <NavLink to="/rules" className={desktopLink} data-testid="nav-rules"><ScrollText className="w-3.5 h-3.5" /> Regole</NavLink>
              <NavLink to="/billing" className={desktopLink} data-testid="nav-billing"><CreditCard className="w-3.5 h-3.5" /> Abbonamento</NavLink>
              {isAdmin && (
                <NavLink to="/admin" className={desktopLink} data-testid="nav-admin"><Shield className="w-3.5 h-3.5" /> Admin</NavLink>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden lg:flex items-center gap-2 text-zinc-500 font-mono-tel text-xs">
              <span className="w-1.5 h-1.5 bg-yellow-500 pulse-line" />
              <span data-testid="utc-clock">{utc} UTC</span>
            </div>
            {showStatusBadge && (
              <button
                onClick={() => navigate("/billing")}
                className="hidden sm:inline-flex border border-yellow-500/40 bg-yellow-500/10 text-yellow-400 px-2 py-1 text-[10px] font-mono-tel uppercase tracking-wider"
                data-testid="subscription-badge"
              >
                {subStatus === "trial" ? `Trial · ${user.free_uses_remaining ?? 0} verdetti` : subStatus}
              </button>
            )}
            {user && typeof user === "object" && (
              <>
                <NavLink to="/profile" className="hidden sm:flex flex-col items-end leading-tight hover:opacity-80" data-testid="profile-link">
                  <span className="text-xs text-zinc-500 font-mono-tel uppercase flex items-center gap-1">
                    <UserCircle className="w-3 h-3" /> Steward
                  </span>
                  <span className="text-sm text-white font-display font-bold uppercase tracking-wide" data-testid="current-user-name">
                    {user.name}
                  </span>
                </NavLink>
                <button onClick={handleLogout} className="hidden md:inline-flex btn-secondary" data-testid="logout-button">
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Esci</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
          <div className="absolute left-0 top-0 bottom-0 w-[84%] max-w-sm bg-zinc-950 border-r border-zinc-800 flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 border-b border-zinc-800">
              <BrandLogo size={32} showText textClassName="text-sm text-white" />
              <button
                onClick={close}
                className="inline-flex items-center justify-center w-9 h-9 border border-zinc-800 text-zinc-300 hover:text-yellow-500"
                aria-label="Chiudi menu"
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {user && typeof user === "object" && (
              <div className="px-4 py-4 border-b border-zinc-800">
                <div className="text-[10px] text-zinc-500 font-mono-tel uppercase tracking-widest">Steward</div>
                <div className="font-display font-black uppercase text-white text-lg leading-tight" data-testid="mobile-user-name">
                  {user.name}
                </div>
                <div className="font-mono-tel text-[11px] text-zinc-500 truncate">{user.email}</div>
                {subStatus && (
                  <button
                    onClick={() => {
                      close();
                      navigate("/billing");
                    }}
                    className={`mt-2 inline-block border px-2 py-1 text-[10px] font-mono-tel uppercase tracking-wider ${
                      subStatus === "active"
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                        : subStatus === "trial"
                        ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-400"
                        : "border-red-500/40 bg-red-500/10 text-red-400"
                    }`}
                  >
                    {subStatus === "trial"
                      ? `Trial · ${user.free_uses_remaining ?? 0} verdetti`
                      : subStatus}
                  </button>
                )}
              </div>
            )}

            <nav className="flex-1 overflow-y-auto py-2" data-testid="mobile-nav">
              <NavLink to="/dashboard" onClick={close} className={mobileLink} data-testid="mobile-nav-dashboard">
                <Gauge className="w-4 h-4" /> Analizza
              </NavLink>
              <NavLink to="/history" onClick={close} className={mobileLink} data-testid="mobile-nav-history">
                <History className="w-4 h-4" /> Storico
              </NavLink>
              <NavLink to="/rules" onClick={close} className={mobileLink} data-testid="mobile-nav-rules">
                <ScrollText className="w-4 h-4" /> Regole della Lega
              </NavLink>
              <NavLink to="/billing" onClick={close} className={mobileLink} data-testid="mobile-nav-billing">
                <CreditCard className="w-4 h-4" /> Abbonamento
              </NavLink>
              <NavLink to="/profile" onClick={close} className={mobileLink} data-testid="mobile-nav-profile">
                <UserCircle className="w-4 h-4" /> Profilo
              </NavLink>
              {isAdmin && (
                <NavLink to="/admin" onClick={close} className={mobileLink} data-testid="mobile-nav-admin">
                  <Shield className="w-4 h-4" /> Pannello Admin
                </NavLink>
              )}
            </nav>

            <div className="px-4 py-4 border-t border-zinc-800 flex items-center justify-between">
              <span className="font-mono-tel text-[10px] text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 pulse-line" /> {utc} UTC
              </span>
              <button onClick={handleLogout} className="btn-secondary text-xs" data-testid="mobile-logout-button">
                <LogOut className="w-3.5 h-3.5" /> Esci
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
